var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { expect, test } from 'vitest';
import createStreamingWorkerFunction from "./createStreamingWorkerFunction.js";
import createNonStreamingWorkerFunction from "./createWorkerFunction.js";
const INTER_THREAD_COMMUNICATION_DELAY = 150;
export default function runTests(env) {
    test('create and call a function', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createNonStreamingWorkerFunction(env, (a, b) => a + b);
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.call(1, 2); })).rejects.toThrow('Not started');
        workerFn.start();
        expect(() => workerFn.start()).toThrow('Was started');
        expect(yield workerFn.call(1, 2)).toEqual(3);
        workerFn.stop();
        // Stopping a function multiple times doesn't throw any error.
        // expect(() => workerFn.stop()).toThrow('Was stopped')
        workerFn.stop();
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.call(1, 2); })).rejects.toThrow('Was stopped');
    }));
    test('create and call a function once', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createNonStreamingWorkerFunction(env, (a, b) => a + b);
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.call(1, 2); })).rejects.toThrow('Not started');
        expect(workerFn.inputLatency).toBeUndefined();
        expect(workerFn.outputLatency).toBeUndefined();
        expect(yield workerFn.callOnce(1, 2)).toEqual(3);
        expect(workerFn.inputLatency).toBeTypeOf('number');
        expect(workerFn.outputLatency).toBeTypeOf('number');
        expect(() => workerFn.start()).toThrow('Was stopped');
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.callOnce(1, 2); })).rejects.toThrow('Was stopped');
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.call(1, 2); })).rejects.toThrow('Was stopped');
        // Stopping a function multiple times doesn't throw any error.
        // expect(() => workerFn.stop()).toThrow('Was stopped')
        workerFn.stop();
    }));
    test('create and listen to a function', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createStreamingWorkerFunction(env, (send) => {
            let count = 0;
            // An streaming worker function could receive multiple arguments from the main thread.
            return ([a, b]) => {
                count++;
                // An streaming worker function could send multiple arguments to the main thread.
                send([a + b, count]);
                send(String(a + b + count));
            };
        });
        expect(workerFn.inputLatency).toBeUndefined();
        expect(workerFn.outputLatency).toBeUndefined();
        let responseCounter = 0;
        // First time adding an error listener — the listener doesn't do anything.
        workerFn.onOutput((error) => {
            expect(true).toEqual(false);
        });
        // First time adding an output listener — overwrites the first one.
        workerFn.onOutput((output) => {
            expect(workerFn.inputLatency).toBeTypeOf('number');
            expect(workerFn.outputLatency).toBeTypeOf('number');
            responseCounter++;
            if (responseCounter === 1) {
                expect(output).toBeInstanceOf(Array);
                expect(output[0]).toEqual(3);
                expect(output[1]).toEqual(1);
            }
            else if (responseCounter === 2) {
                expect(output).toBeTypeOf('string');
                expect(output).toEqual('4');
            }
            else if (responseCounter === 3) {
                expect(output).toBeInstanceOf(Array);
                expect(output[0]).toEqual(3);
                expect(output[1]).toEqual(2);
            }
            else if (responseCounter === 4) {
                expect(output).toBeTypeOf('string');
                expect(output).toEqual('5');
            }
            else {
                throw new Error('Unexpected response');
            }
        });
        workerFn.onError((error) => {
            throw new Error('Unexpected error');
        });
        expect(() => workerFn.send([1, 2])).toThrow('Not started');
        workerFn.start();
        expect(() => workerFn.onOutput(() => { })).toThrow('Was started');
        expect(() => workerFn.onError(() => { })).toThrow('Was started');
        expect(() => workerFn.start()).toThrow('Was started');
        workerFn.send([1, 2]);
        expect(responseCounter).toEqual(0);
        workerFn.send([1, 2]);
        yield delay(INTER_THREAD_COMMUNICATION_DELAY);
        expect(responseCounter).toEqual(4);
        workerFn.stop();
        // Stopping a function multiple times doesn't throw any error.
        // expect(() => workerFn.stop()).toThrow('Was stopped')
        workerFn.stop();
        expect(() => workerFn.send([1, 2])).toThrow('Was stopped');
    }));
    test('not allow changing dependencies of an already-aliased function', () => __awaiter(this, void 0, void 0, function* () {
        // Dependency.
        const c = 1;
        // Create a function and assign an alias.
        const workerFn = createNonStreamingWorkerFunction(env, (a, b) => a + b + c);
        workerFn.addDependencies(() => [c]);
        workerFn.alias('sum');
        expect(() => {
            workerFn.alias('a+b');
        }).toThrow('Has alias');
        expect(() => {
            workerFn.addDependencies(() => [
                createTestError,
                TestError,
                TEST_ERROR_PROPERTIES
            ]);
        }).toThrow('Has alias');
        // Create a function from an alias.
        const workerFn2 = createNonStreamingWorkerFunction(env, 'sum');
        expect(() => {
            workerFn2.alias('a+b');
        }).toThrow('Has alias');
        expect(() => {
            workerFn2.addDependencies(() => [
                createTestError,
                TestError,
                TEST_ERROR_PROPERTIES
            ]);
        }).toThrow('Has alias');
        // Test that an alias also captures the dependencies.
        expect(yield workerFn2.callOnce(1, 2)).toEqual(4);
    }));
    test('throw when alias is not found', () => __awaiter(this, void 0, void 0, function* () {
        expect(() => {
            createNonStreamingWorkerFunction(env, 'alias-not-exists');
        }).toThrow('Not found');
    }));
    test('clonable dependencies', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createNonStreamingWorkerFunction(env, (a, b) => a + b);
        const a = 1;
        const b = 2;
        const c = true;
        const d = new Date();
        const e = [1, 'a'];
        const f = { a: 1, b: [2] };
        const g = new Uint8Array(8);
        workerFn.addDependencies(() => [a, b, c, d, e, f, g]);
        workerFn.start();
        workerFn.stop();
    }));
    test('return error when called', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createNonStreamingWorkerFunction(env, (a, b) => {
            return createTestError();
        });
        workerFn.addDependencies(() => [
            createTestError,
            TestError,
            TEST_ERROR_PROPERTIES
        ]);
        // Start the worker function.
        workerFn.start();
        // Return (not throw) an error from the worker function.
        const error = yield workerFn.call(1, 2);
        // Validate that the error properties have been reset.
        expect(TEST_ERROR_PROPERTIES.code).toBeTypeOf('string');
        expect(error.code).toBe(undefined);
        expect(TEST_ERROR_PROPERTIES.name).not.toEqual('Error');
        expect(error.name).toEqual('Error');
        // Validate that the error class was reset to `Error`
        expect(error).toBeInstanceOf(Error);
        expect(error).not.toBeInstanceOf(TestError);
        // Stop the worker function.
        workerFn.stop();
    }));
    test('catch error when called', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createNonStreamingWorkerFunction(env, (a, b) => {
            throw createTestError();
        });
        workerFn.addDependencies(() => [
            createTestError,
            TestError,
            TEST_ERROR_PROPERTIES
        ]);
        workerFn.start();
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.call(1, 2); })).rejects.toThrow(expect.objectContaining(TEST_ERROR_PROPERTIES));
        // These two assertions could also be added here somehow:
        // expect(error).toBeInstanceOf(Error)
        // expect(error).not.toBeInstanceOf(TestError)
    }));
    test('catch error when called once', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createNonStreamingWorkerFunction(env, (a, b) => {
            throw createTestError();
        });
        workerFn.addDependencies(() => [
            createTestError,
            TestError,
            TEST_ERROR_PROPERTIES
        ]);
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.callOnce(1, 2); })).rejects.toThrow(expect.objectContaining(TEST_ERROR_PROPERTIES));
        // These two assertions could also be added here somehow:
        // expect(error).toBeInstanceOf(Error)
        // expect(error).not.toBeInstanceOf(TestError)
    }));
    test('catch error when called listened', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createStreamingWorkerFunction(env, (send) => {
            return (input) => {
                throw createTestError();
            };
        });
        workerFn.addDependencies(() => [
            createTestError,
            TestError,
            TEST_ERROR_PROPERTIES
        ]);
        workerFn.onOutput((output) => {
            throw new Error('Unexpected response');
        });
        let errorCounter = 0;
        // First time adding an error listener — the listener doesn't do anything.
        workerFn.onError((error) => {
            expect(true).toEqual(false);
        });
        // Second time adding an error listener — overwrites the first listener.
        workerFn.onError((error) => {
            expect(error).toEqual(expect.objectContaining(TEST_ERROR_PROPERTIES));
            expect(error).toBeInstanceOf(Error);
            expect(error).not.toBeInstanceOf(TestError);
            errorCounter++;
        });
        workerFn.start();
        workerFn.send(1);
        expect(errorCounter).toEqual(0);
        yield delay(INTER_THREAD_COMMUNICATION_DELAY);
        expect(errorCounter).toEqual(1);
        workerFn.stop();
    }));
    test('add dependencies that\'re "native" global classes or functions', () => __awaiter(this, void 0, void 0, function* () {
        const fn = (length) => new Uint8Array(length);
        const workerFn = createNonStreamingWorkerFunction(env, fn);
        const result = yield workerFn.callOnce(1);
        expect(result).toBeInstanceOf(Uint8Array);
    }));
    test('add dependencies and dependencies of dependencies', () => __awaiter(this, void 0, void 0, function* () {
        const d = 2;
        const c = () => d + 1;
        const fn = (a, b) => a + b + c();
        let workerFn = createNonStreamingWorkerFunction(env, fn);
        workerFn.start();
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.call(1, 2); })).rejects.toThrow('c is not defined');
        workerFn = createNonStreamingWorkerFunction(env, fn);
        workerFn.addDependencies(() => [c]);
        workerFn.start();
        yield expect(() => __awaiter(this, void 0, void 0, function* () { return yield workerFn.call(1, 2); })).rejects.toThrow('d is not defined');
        workerFn = createNonStreamingWorkerFunction(env, fn);
        workerFn.addDependencies(() => [c, d]);
        workerFn.start();
        expect(yield workerFn.call(1, 2)).toEqual(6);
        workerFn.stop();
        workerFn = createNonStreamingWorkerFunction(env, fn);
        // @ts-expect-error
        expect(() => workerFn.addDependencies([c, d])).toThrow('function');
    }));
    test('create transfer list for input arguments (call)', () => __awaiter(this, void 0, void 0, function* () {
        const call = (getInputTransferList) => __awaiter(this, void 0, void 0, function* () {
            const workerFn = createNonStreamingWorkerFunction(env, (buffer) => buffer.byteLength);
            const buffer = new ArrayBuffer(1);
            const view = new Uint8Array(buffer);
            view.fill(255);
            if (getInputTransferList) {
                workerFn.inputTransferList(getInputTransferList);
            }
            workerFn.start();
            expect(buffer.byteLength).toEqual(1);
            yield workerFn.call(buffer);
            expect(buffer.byteLength).toEqual(getInputTransferList ? 0 : 1);
            workerFn.stop();
        });
        // Without `inputTransferList`
        yield call();
        // With `inputTransferList`
        yield call((buffer) => [buffer]);
    }));
    test('create transfer list for output result (call)', () => __awaiter(this, void 0, void 0, function* () {
        const test = (alias) => __awaiter(this, void 0, void 0, function* () {
            let workerFn = createNonStreamingWorkerFunction(env, () => {
                const buffer = new ArrayBuffer(1);
                const view = new Uint8Array(buffer);
                view.fill(255);
                setTimeout(() => {
                    if (buffer.byteLength !== 0) {
                        throw new Error('Expected output buffer to have been transferred (call)');
                    }
                }, 0);
                return buffer;
            });
            workerFn.outputTransferList((buffer) => [buffer]);
            if (alias) {
                workerFn.alias(alias);
                workerFn = createNonStreamingWorkerFunction(env, alias);
            }
            workerFn.start();
            const buffer = yield workerFn.call();
            expect(buffer.byteLength).toEqual(1);
            // Wait for the `setTimeout()` call in the function body to finish.
            yield delay(INTER_THREAD_COMMUNICATION_DELAY);
            workerFn.stop();
        });
        // Without alias.
        yield test();
        // With alias.
        yield test('outputTransferList-call');
    }));
    test('streaming function: able to respond before receiving any input data', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createStreamingWorkerFunction(env, (send) => {
            send(1);
            return () => { };
        });
        let result;
        workerFn.onOutput((number) => {
            result = number;
        });
        expect(result).toBeUndefined();
        expect(workerFn.inputLatency).toBeUndefined();
        expect(workerFn.outputLatency).toBeUndefined();
        workerFn.start();
        yield delay(INTER_THREAD_COMMUNICATION_DELAY);
        expect(result).toEqual(1);
        expect(workerFn.inputLatency).toBeUndefined();
        expect(workerFn.outputLatency).toBeTypeOf('number');
        workerFn.stop();
    }));
    test('create transfer list for input arguments (stream)', () => __awaiter(this, void 0, void 0, function* () {
        const test = (alias, getInputTransferList) => __awaiter(this, void 0, void 0, function* () {
            let workerFn = createStreamingWorkerFunction(env, (send) => {
                return (buffer) => { };
            });
            const buffer = new ArrayBuffer(1);
            const view = new Uint8Array(buffer);
            view.fill(255);
            if (getInputTransferList) {
                workerFn.inputTransferList(getInputTransferList);
            }
            if (alias) {
                workerFn.alias(alias);
                workerFn = createStreamingWorkerFunction(env, alias);
            }
            workerFn.start();
            expect(buffer.byteLength).toEqual(1);
            yield workerFn.send(buffer);
            expect(buffer.byteLength).toEqual(getInputTransferList ? 0 : 1);
            workerFn.stop();
        });
        // Without alias. Without `inputTransferList`.
        yield test(undefined);
        // Without alias. With `inputTransferList`.
        yield test(undefined, (buffer) => [buffer]);
        // With alias. Without `inputTransferList`.
        yield test('outputTransferList-stream');
        // With alias. With `inputTransferList`.
        yield test('outputTransferList-stream', (buffer) => [buffer]);
    }));
    test('create transfer list for output result (stream)', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn = createStreamingWorkerFunction(env, (send) => {
            return () => __awaiter(this, void 0, void 0, function* () {
                const buffer = new ArrayBuffer(1);
                const view = new Uint8Array(buffer);
                view.fill(255);
                send(buffer);
                setTimeout(() => {
                    if (buffer.byteLength !== 0) {
                        throw new Error('Expected output buffer to have been transferred (stream)');
                    }
                }, 0);
            });
        });
        let outputReceived = false;
        workerFn.onOutput((buffer) => {
            outputReceived = true;
            expect(buffer.byteLength).toEqual(1);
        });
        workerFn.outputTransferList((buffer) => [buffer]);
        workerFn.start();
        yield workerFn.send(1);
        expect(outputReceived).toEqual(false);
        yield delay(INTER_THREAD_COMMUNICATION_DELAY);
        expect(outputReceived).toEqual(true);
        workerFn.stop();
    }));
    test('cache (non-streaming function)', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn_ = createNonStreamingWorkerFunction(env, (a, b) => a + b);
        workerFn_.alias('fn1');
        expect(() => {
            createNonStreamingWorkerFunction(env, 'fn2');
        }).toThrow('Not found');
        const workerFn = createNonStreamingWorkerFunction(env, 'fn1');
        workerFn.start();
        expect(yield workerFn.call(1, 2)).toEqual(3);
        workerFn.stop();
        const workerFn2 = createNonStreamingWorkerFunction(env, 'fn1');
        workerFn2.start();
        expect(yield workerFn2.call(1, 2)).toEqual(3);
        workerFn2.stop();
    }));
    test('cache (streaming function)', () => __awaiter(this, void 0, void 0, function* () {
        const workerFn_ = createStreamingWorkerFunction(env, (send) => {
            return (number) => {
                send(number * number);
            };
        });
        workerFn_.alias('fn1');
        expect(() => {
            createStreamingWorkerFunction(env, 'fn2');
        }).toThrow('Not found');
        const workerFn = createStreamingWorkerFunction(env, 'fn1');
        let output;
        workerFn.onOutput((result) => {
            output = result;
        });
        workerFn.start();
        expect(output).toBeUndefined();
        workerFn.send(2);
        yield delay(INTER_THREAD_COMMUNICATION_DELAY);
        expect(output).toEqual(4);
        workerFn.stop();
        const workerFn2 = createStreamingWorkerFunction(env, 'fn1');
        let output2;
        workerFn2.onOutput((result) => {
            output2 = result;
        });
        workerFn2.start();
        workerFn2.send(3);
        expect(output2).toBeUndefined();
        yield delay(INTER_THREAD_COMMUNICATION_DELAY);
        expect(output2).toEqual(9);
        workerFn2.stop();
    }));
}
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
const TEST_ERROR_PROPERTIES = {
    message: 'This is a test',
    name: 'TestError',
    code: 'TEST_CODE'
};
function createTestError() {
    return new TestError(TEST_ERROR_PROPERTIES.message);
}
class TestError extends Error {
    constructor(message) {
        super(message);
        this.name = TEST_ERROR_PROPERTIES.name;
        this.code = TEST_ERROR_PROPERTIES.code;
    }
}
